/**
 * Converts a FileUploadItem from the upload store to a unified FileRow
 *
 * @param upload - The upload item from the file-upload store
 * @param userId - Optional user ID to associate with the file
 * @param username - Optional username to associate with the file
 * @returns FileRow representation of the upload
 */
import type { EnhancedRagFileMetadataDto } from '@inference.engine/assets-api-client/web';
import type { FileUploadItem } from '@/features/knowledge-bases/model';
import type { FileRow } from '@/features/knowledge-bases/model';

/**
 * Converts a persisted RAG file DTO from the assets API into a unified FileRow.
 * Normalizes the DTO's ISO date strings to `Date` so the row satisfies FileRow's
 * contract without an `as unknown` cast at the call site.
 */
export const mapRagFileMetadataToFileRow = (file: EnhancedRagFileMetadataDto): FileRow => ({
  id: file.id,
  fileName: file.fileName,
  knowledgeBaseId: file.knowledgeBaseId,
  mimeType: file.mimeType,
  fileHash: file.fileHash,
  summary: file.summary,
  tags: file.tags,
  createdBy: file.createdBy,
  createdByUsername: file.createdByUsername,
  assetId: file.assetId,
  created: file.created ? new Date(file.created) : undefined,
  updated: file.updated ? new Date(file.updated) : undefined,
});

export const mapUploadItemToFileRow = (
  upload: FileUploadItem,
  userId?: string,
  username?: string,
): FileRow => ({
  id: upload.id,
  fileName: upload.file.name,
  knowledgeBaseId: upload.knowledgeBaseId,
  file: upload.file,
  created: upload.startedAt,
  createdBy: userId,
  createdByUsername: username,
  isUploading: true,
  uploadStatus: upload.status,
  uploadError: upload.error,
  startedAt: upload.startedAt,
  completedAt: upload.completedAt,
  documentId: upload.documentId,
  abortController: upload.abortController,
});
